import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { STUDENT_NAV } from "@/lib/studentNav";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, VideoOff, X } from "lucide-react";

const VIDEO_ID = "tCDvOQI3pco";
let youtubeApiPromise;

function loadYouTubeApi() {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (!youtubeApiPromise) {
    youtubeApiPromise = new Promise((resolve, reject) => {
      const previousReady = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        previousReady?.();
        resolve(window.YT);
      };
      const script = document.createElement("script");
      script.src = "https://www.youtube.com/iframe_api";
      script.async = true;
      script.onerror = () => {
        youtubeApiPromise = null;
        reject(new Error("YouTube player could not load"));
      };
      document.head.appendChild(script);
    });
  }
  return youtubeApiPromise;
}

export default function AttemptInstructions() {
  const { paperId } = useParams();
  const navigate = useNavigate();
  const [videoUnavailable, setVideoUnavailable] = useState(false);
  const playerContainer = useRef(null);
  const capturePath = `/student/capture/${paperId}`;

  useEffect(() => {
    let cancelled = false;
    let player;
    setVideoUnavailable(false);
    loadYouTubeApi().then((YT) => {
      if (cancelled || !playerContainer.current) return;
      player = new YT.Player(playerContainer.current, {
        width: "100%",
        height: "100%",
        videoId: VIDEO_ID,
        playerVars: { playsinline: 1, rel: 0 },
        events: {
          onStateChange: (event) => {
            if (event.data === YT.PlayerState.ENDED) navigate(capturePath, { replace: true });
          },
          onError: () => setVideoUnavailable(true),
        },
      });
    }).catch(() => {
      if (!cancelled) setVideoUnavailable(true);
    });
    return () => {
      cancelled = true;
      player?.destroy();
    };
  }, [capturePath, navigate]);

  return (
    <DashboardShell nav={STUDENT_NAV} title="Before you attempt">
      <div className="mx-auto max-w-3xl space-y-6">
        <div className="rounded-lg border border-border bg-background p-5 sm:p-6">
          <div className="flex items-start justify-between gap-3">
            <h2 className="font-heading text-xl font-bold">Watch the instructions</h2>
            {!videoUnavailable && (
              <Button
                variant="outline"
                size="sm"
                className="shrink-0 gap-1.5"
                onClick={() => navigate(capturePath, { replace: true })}
                data-testid="close-instruction-video"
              >
                <X className="h-4 w-4" /> Close video
              </Button>
            )}
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            Your attempt screen opens when the video finishes or you close it.
          </p>

          {videoUnavailable ? (
            <div className="mt-5 flex aspect-video flex-col items-center justify-center gap-3 rounded-lg bg-secondary p-6 text-center" role="status" data-testid="instruction-video-unavailable">
              <VideoOff className="h-10 w-10 text-muted-foreground" />
              <p className="font-medium">Instruction video is not available yet.</p>
              <p className="text-sm text-muted-foreground">You can still continue to your attempt.</p>
            </div>
          ) : <div className="mt-5 aspect-video w-full overflow-hidden rounded-lg bg-black" data-testid="attempt-instruction-video"><div ref={playerContainer} className="h-full w-full" /></div>}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button asChild variant="outline" className="gap-2 rounded-full">
            <Link to="/student"><ArrowLeft className="h-4 w-4" /> Back to exams</Link>
          </Button>
          {videoUnavailable && (
            <Button asChild className="gap-2 rounded-full" data-testid="continue-to-attempt">
              <Link to={capturePath}>Continue to attempt <ArrowRight className="h-4 w-4" /></Link>
            </Button>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}
